import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Switch } from "theme-ui";
import { noop } from "sites-common/utils/lodash";
import NetBankingSection from "../NetBankingSection";
import WalletSection from "../WalletSection";
import ECSSection from "../ECSSection";
import { FlexColumn, RadiosWithLabel } from "../../../index";

export const useOfflineDonation = (args = {}) => {
  const {
    showOfflineRecurringSection = false,
    offlineSectionDetails = {
      payment_option:
        "Standing Instruction with donor’s bank for making recurring donations",
      standingInstructionDetails: {
        payment_mode: "Internet banking",
        netBankingDetails: {
          donor_bank: "YES BANK",
          standing_date: null,
          donor_ifsc_code: "",
        },
        walletDetails: {
          wallet_mobile: "",
          standing_date: null,
        },
      },
    },
    onChange = noop,
  } = args;
  const onChangeOfflineSection = useCallback(
    (update) => {
      onChange({
        showOfflineRecurringSection,
        offlineSectionDetails,
        donation_type: "OFFLINE",
        ...update,
      });
    },
    [onChange, showOfflineRecurringSection, offlineSectionDetails]
  );

  const onChangeOfflineSectionDetails = React.useCallback(
    (update) => {
      onChangeOfflineSection({
        offlineSectionDetails: {
          ...offlineSectionDetails,
          ...update,
        },
      });
    },
    [offlineSectionDetails, onChangeOfflineSection]
  );

  const onChangeStandingInstructionDetails = React.useCallback(
    (update) => {
      onChangeOfflineSectionDetails({
        standingInstructionDetails: {
          ...offlineSectionDetails.standingInstructionDetails,
          ...update,
        },
      });
    },
    [
      onChangeOfflineSectionDetails,
      offlineSectionDetails.standingInstructionDetails,
    ]
  );
  const offlineSectionSwitch = React.useMemo(
    () => ({
      disabled: true,
      defaultChecked: showOfflineRecurringSection,
      onChange: ({ target: { checked } }) =>
        onChangeOfflineSection({
          showOfflineRecurringSection: checked,
        }),
    }),
    [onChangeOfflineSection, showOfflineRecurringSection]
  );

  const donationTypeOptions = React.useMemo(
    () => [
      {
        value: "ONLINE",
        label: "Online",
      },
      {
        value: "OFFLINE",
        label: "Offline",
      },
    ],
    []
  );

  const paymentOptions = React.useMemo(
    () => [
      {
        value:
          "Standing Instruction with donor’s bank for making recurring donations",
        label: "Standing Instruction for banking",
      },
      { value: "ECS Donations", label: "ECS Donations" },
    ],
    []
  );

  const paymentOptionRadios = React.useMemo(
    () => ({
      name: "payment_option",
      options: paymentOptions,
      value: offlineSectionDetails.payment_option,
      onChange: (payment_option) =>
        onChangeOfflineSectionDetails({
          payment_option,
        }),
    }),
    [onChangeOfflineSectionDetails, paymentOptions, offlineSectionDetails]
  );

  const paymentModes = React.useMemo(
    () => [
      { value: "Internet banking", label: "Net Banking" },
      { value: "WALLET", label: "Wallet" },
    ],
    []
  );

  const paymentModeRadios = React.useMemo(
    () => ({
      name: "payment_mode",
      options: paymentModes,
      value: offlineSectionDetails.standingInstructionDetails.payment_mode,
      onChange: (payment_mode) =>
        onChangeStandingInstructionDetails({
          payment_mode,
        }),
    }),
    [offlineSectionDetails, paymentModes, onChangeStandingInstructionDetails]
  );

  const netBankingSection = React.useMemo(
    () => ({
      ...offlineSectionDetails.standingInstructionDetails.netBankingDetails,
      onChange: (netBankingDetails) =>
        onChangeStandingInstructionDetails({
          netBankingDetails,
        }),
    }),
    [
      offlineSectionDetails.standingInstructionDetails.netBankingDetails,
      onChangeStandingInstructionDetails,
    ]
  );

  const walletSection = React.useMemo(
    () => ({
      ...offlineSectionDetails.standingInstructionDetails.walletDetails,
      onChange: (walletDetails) =>
        onChangeStandingInstructionDetails({ walletDetails }),
    }),
    [
      offlineSectionDetails.standingInstructionDetails.walletDetails,
      onChangeStandingInstructionDetails,
    ]
  );

  return {
    offlineSectionSwitch,
    donationTypeOptions,
    paymentOptionRadios,
    paymentModeRadios,
    netBankingSection,
    walletSection,
    showStandingInstructionSection: React.useMemo(
      () => offlineSectionDetails.payment_option === paymentOptions[0].value,
      [offlineSectionDetails.payment_option, paymentOptions]
    ),
    showECSSection: React.useMemo(
      () => offlineSectionDetails.payment_option === paymentOptions[1].value,
      [offlineSectionDetails.payment_option, paymentOptions]
    ),
    showNetBankingSection: React.useMemo(
      () =>
        offlineSectionDetails.standingInstructionDetails.payment_mode ===
        paymentModes[0].value,
      [
        offlineSectionDetails.standingInstructionDetails.payment_mode,
        paymentModes,
      ]
    ),
    showWalletSection: React.useMemo(
      () =>
        offlineSectionDetails.standingInstructionDetails.payment_mode ===
        paymentModes[1].value,
      [
        offlineSectionDetails.standingInstructionDetails.payment_mode,
        paymentModes,
      ]
    ),
    showOfflineRecurringSection,
  };
};

const OfflineDonationSection = ({
  offlineSectionSwitch,
  showOfflineRecurringSection,
  paymentOptionRadios,
  showStandingInstructionSection,
  paymentModeRadios,
  showNetBankingSection,
  netBankingSection,
  showWalletSection,
  walletSection,
  showECSSection,
}) => (
  <FlexColumn gap="13px" sx={{ color: "text", mt: 2 }}>
    <Switch label="Offline" sx={{ color: "text" }} {...offlineSectionSwitch} />
    {showOfflineRecurringSection && (
      <FlexColumn gap="13px" sx={{ color: "text" }}>
        <>
          <RadiosWithLabel
            label="Payment Option"
            radiosWrapper={Box}
            style={{ fontSize: "13px" }}
            {...paymentOptionRadios}
          />

          {/* STANDING INSTRUCTION SECTION */}
          {showStandingInstructionSection && (
            <>
              <RadiosWithLabel
                label="Payment Mode"
                style={{ fontSize: "13px" }}
                {...paymentModeRadios}
              />

              {/* NET BANKING SECTION */}
              {showNetBankingSection && (
                <NetBankingSection {...netBankingSection} />
              )}

              {/* SHOW WALLET SECTION */}
              {showWalletSection && <WalletSection {...walletSection} />}
            </>
          )}

          {/* ECS Section */}
          {showECSSection && <ECSSection />}
        </>
      </FlexColumn>
    )}
  </FlexColumn>
);

OfflineDonationSection.propTypes = {
  isMonthlyRecurringDonation: PropTypes.bool,
  offlineSectionSwitch: PropTypes.shape({}),
  donationTypeRadios: PropTypes.shape({}),
  onlineSection: PropTypes.shape({}),
  paymentOptionRadios: PropTypes.shape({}),
  paymentModeRadios: PropTypes.shape({}),
  netBankingSection: PropTypes.shape({}),
  walletSection: PropTypes.shape({}),
  showOnlineSection: PropTypes.bool,
  showOfflineRecurringSection: PropTypes.bool,
  showStandingInstructionSection: PropTypes.bool,
  showECSSection: PropTypes.bool,
  showNetBankingSection: PropTypes.bool,
  showWalletSection: PropTypes.bool,
};

OfflineDonationSection.defaultProps = {
  isMonthlyRecurringDonation: false,
  offlineSectionSwitch: {},
  donationTypeRadios: {},
  onlineSection: {},
  paymentOptionRadios: {},
  paymentModeRadios: {},
  netBankingSection: {},
  walletSection: {},
  showOnlineSection: false,
  showOfflineRecurringSection: false,
  showECSSection: false,
  showNetBankingSection: false,
  showWalletSection: false,
  showStandingInstructionSection: false,
};

export default React.memo(OfflineDonationSection);
